import React, { useRef, useState, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { OPopper } from '@src/shared';

import { useVirtualBackgroundStore, DEFAULT_VB_CONFIG, useMediaSettingsStore } from '@src/stores';
import { applyPreset } from '@src/stores/virtualBackground.presets';

import { VIDEO_RESOLUTION_PRESETS, TVideoResolutionPreset } from '@src/utils/videoConstraints.utils';

import { TVBPreset } from '@src/types';

import styles from './AdvancedSettings.module.scss';

export const AdvancedSettings: React.FC = () => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [cpuUsage, setCpuUsage] = useState<number>(15);

  const {
    config,
    setConfig,
    isAdvancedSettingsOpen,
    setIsAdvancedSettingsOpen,
    selectedEffect,
    setAutoPerformanceMode,
  } = useVirtualBackgroundStore(
    useShallow((state) => ({
      config: state.config,
      setConfig: state.setConfig,
      isAdvancedSettingsOpen: state.isAdvancedSettingsOpen,
      setIsAdvancedSettingsOpen: state.setIsAdvancedSettingsOpen,
      selectedEffect: state.selectedEffect,
      setAutoPerformanceMode: state.setAutoPerformanceMode,
    }))
  );

  // Get video resolution settings from media settings store
  const { selectedVideoResolution, setVideoResolution } = useMediaSettingsStore(
    useShallow((state) => ({
      selectedVideoResolution: state.mediaSettings.selectedVideoResolution,
      setVideoResolution: state.setVideoResolution,
    }))
  );

  // Estimate CPU usage based on settings
  useEffect(() => {
    if (selectedEffect.type === 'none') {
      setCpuUsage(0);
      return;
    }

    // Simple CPU estimation formula
    const fpsImpact = (config.segmentationFPS / 30) * 40; // 0-40%
    const scaleImpact = config.inferenceScale * 30; // 0-30%
    const baseImpact = selectedEffect.type === 'blur' ? 10 : selectedEffect.type === 'animation' ? 20 : 15;

    const estimated = Math.round(baseImpact + fpsImpact + scaleImpact);
    setCpuUsage(Math.min(100, Math.max(0, estimated)));
  }, [config.segmentationFPS, config.inferenceScale, selectedEffect.type]);

  const handlePresetChange = (preset: TVBPreset) => {
    const presetConfig = applyPreset(preset);
    setConfig(presetConfig);
  };

  const handleReset = () => {
    setConfig(DEFAULT_VB_CONFIG);
  };

  return (
    <>
      <button
        ref={anchorRef}
        className={styles.advancedToggleBtn}
        onClick={() => setIsAdvancedSettingsOpen(!isAdvancedSettingsOpen)}
      >
        ⚙️ Advanced Settings
      </button>

      <OPopper
        isOpen={isAdvancedSettingsOpen}
        anchorEl={anchorRef.current}
        onClose={() => setIsAdvancedSettingsOpen(false)}
        position="bottom-start"
        isDismissible={true}
        disableClose={true}
        withArrow={false}
        closeOnScroll={false}
        customStyles={{
          width: window.innerWidth < 600 ? '90vw' : 520,
          boxRoot: {
            backgroundColor: 'var(--tertiary-800, #1a1a2e)',
            border: '1px solid var(--border-color, rgba(108, 92, 231, 0.3))',
            borderRadius: '12px',
            padding: '0',
            maxWidth: '100vw',
          },
        }}
      >
        <div className={styles.container}>
          <div className={styles.header}>
            <h3 className={styles.title}>Advanced Virtual Background Settings</h3>
            <p className={styles.subtitle}>Fine-tune quality and performance</p>
          </div>

          {/* Preset Buttons */}
          <div className={styles.presetSection}>
            <div className={styles.presetButtons}>
              <button
                className={`${styles.presetBtn} ${config.preset === 'lite' ? styles.active : ''}`}
                onClick={() => handlePresetChange('lite')}
              >
                🍃 Lite
              </button>
              <button
                className={`${styles.presetBtn} ${config.preset === 'performance' ? styles.active : ''}`}
                onClick={() => handlePresetChange('performance')}
              >
                ⚡ Performance
              </button>
              <button
                className={`${styles.presetBtn} ${config.preset === 'balanced' ? styles.active : ''}`}
                onClick={() => handlePresetChange('balanced')}
              >
                ⚖️ Balanced
              </button>
              <button
                className={`${styles.presetBtn} ${config.preset === 'quality' ? styles.active : ''}`}
                onClick={() => handlePresetChange('quality')}
              >
                ✨ Quality
              </button>
              <button
                className={`${styles.presetBtn} ${config.preset === 'responsive' ? styles.active : ''}`}
                onClick={() => handlePresetChange('responsive')}
              >
                🏃 Responsive
              </button>
              <button
                className={`${styles.presetBtn} ${config.preset === 'rvm' ? styles.active : ''}`}
                onClick={() => handlePresetChange('rvm')}
              >
                🧠 RVM (AI)
              </button>
            </div>
          </div>

          {/* Performance Settings Section */}
          <div className={styles.section}>
            <h4 className={styles.sectionTitle}>Performance Settings</h4>
            <p className={styles.sectionNote}>
              Optimize CPU usage and battery life. Lower FPS/Scale reduces load but may look choppy.
            </p>

            {/* Auto Performance Mode */}
            <div className={styles.settingRow}>
              <div className={styles.settingHeader}>
                <label>Auto-Detect Performance</label>
              </div>
              <div className={styles.toggleGroup}>
                <button
                  className={`${styles.toggleBtn} ${config.autoPerformanceMode ? styles.active : ''}`}
                  onClick={() => setAutoPerformanceMode(true)}
                >
                  On
                </button>
                <button
                  className={`${styles.toggleBtn} ${!config.autoPerformanceMode ? styles.active : ''}`}
                  onClick={() => setAutoPerformanceMode(false)}
                >
                  Off
                </button>
              </div>
              <span className={styles.settingHint}>
                Automatically adjusts settings based on your device capabilities.
              </span>
            </div>

            {/* Segmentation FPS */}
            <div className={styles.settingRow}>
              <div className={styles.settingHeader}>
                <label>Segmentation FPS</label>
                <span className={styles.settingValue}>{config.segmentationFPS}</span>
              </div>
              <input
                type="range"
                min={1}
                max={30}
                step={1}
                value={config.segmentationFPS}
                onChange={(e) => setConfig({ segmentationFPS: Number(e.target.value), preset: 'custom' })}
                className={styles.slider}
              />
              <span className={styles.settingHint}>
                Controls how many times per second the AI analyzes the frame. Lower values save significant CPU.
              </span>
            </div>

            {/* Inference Scale */}
            <div className={styles.settingRow}>
              <div className={styles.settingHeader}>
                <label>Inference Scale</label>
                <span className={styles.settingValue}>{config.inferenceScale.toFixed(2)}</span>
              </div>
              <input
                type="range"
                min={0.2}
                max={1.0}
                step={0.05}
                value={config.inferenceScale}
                onChange={(e) => setConfig({ inferenceScale: Number(e.target.value), preset: 'custom' })}
                className={styles.slider}
              />
              <span className={styles.settingHint}>
                Downscales image before processing. 0.5x is usually sufficient and much faster.
              </span>
            </div>

            {/* Hardware Acceleration */}
            <div className={styles.settingRow}>
              <div className={styles.settingHeader}>
                <label>Hardware Acceleration</label>
              </div>
              <div className={styles.toggleGroup}>
                <button
                  className={`${styles.toggleBtn} ${config.delegate === 'GPU' ? styles.active : ''}`}
                  onClick={() => setConfig({ delegate: 'GPU', preset: 'custom' })}
                >
                  GPU
                </button>
                <button
                  className={`${styles.toggleBtn} ${config.delegate === 'CPU' ? styles.active : ''}`}
                  onClick={() => setConfig({ delegate: 'CPU', preset: 'custom' })}
                >
                  CPU
                </button>
              </div>
              <span className={styles.settingHint}>
                Delegates processing to the graphics card. Disable if you experience crashes.
              </span>
            </div>

            {/* Segmentation Backend */}
            <div className={styles.settingRow}>
              <div className={styles.settingHeader}>
                <label>AI Engine</label>
              </div>
              <div className={styles.toggleGroup}>
                <button
                  className={`${styles.toggleBtn} ${config.segmentationBackend === 'mediapipe' ? styles.active : ''}`}
                  onClick={() => setConfig({ segmentationBackend: 'mediapipe', preset: 'custom' })}
                >
                  MediaPipe
                </button>
                <button
                  className={`${styles.toggleBtn} ${config.segmentationBackend === 'rvm' ? styles.active : ''}`}
                  onClick={() => setConfig({ segmentationBackend: 'rvm', preset: 'custom' })}
                >
                  RVM (Beta)
                </button>
              </div>
              <span className={styles.settingHint}>
                Robust Video Matting provides superior edge detail but consumes more resources.
              </span>
            </div>
          </div>

          {/* Quality Settings Section */}
          <div className={styles.section}>
            <h4 className={styles.sectionTitle}>Quality Settings</h4>
            <p className={styles.sectionNote}>
              Fine-tune mask precision. Smoothing reduces jitter, while Edge Blur softens the cutout.
            </p>

            {/* Smoothing */}
            <div className={styles.settingRow}>
              <div className={styles.settingHeader}>
                <label>Smoothing</label>
                <span className={styles.settingValue}>{config.smoothing.toFixed(2)}</span>
              </div>
              <input
                type="range"
                min={0}
                max={1}
                step={0.1}
                value={config.smoothing}
                onChange={(e) => setConfig({ smoothing: Number(e.target.value), preset: 'custom' })}
                className={styles.slider}
              />
              <span className={styles.settingHint}>
                Reduces jitter by blending with previous frames. High values may cause ghosting.
              </span>
            </div>

            {/* Edge Blur */}
            <div className={styles.settingRow}>
              <div className={styles.settingHeader}>
                <label>Edge Blur</label>
                <span className={styles.settingValue}>{config.edgeBlur}px</span>
              </div>
              <input
                type="range"
                min={0}
                max={20}
                step={1}
                value={config.edgeBlur}
                onChange={(e) => setConfig({ edgeBlur: Number(e.target.value), preset: 'custom' })}
                className={styles.slider}
              />
              <span className={styles.settingHint}>
                Softens the boundary between you and the background. Minimum 3px for anti-flicker.
              </span>
            </div>
          </div>

          {/* Advanced Settings Section */}
          <div className={styles.section}>
            <h4 className={styles.sectionTitle}>Advanced Settings</h4>
            <p className={styles.sectionNote}>Technical controls for rendering engine selection.</p>

            {/* Renderer Selection */}
            <div className={styles.settingRow}>
              <div className={styles.settingHeader}>
                <label>Rendering Engine</label>
              </div>
              <div className={styles.toggleGroup}>
                <button
                  className={`${styles.toggleBtn} ${config.rendererType === 'webgl' ? styles.active : ''}`}
                  onClick={() => setConfig({ rendererType: 'webgl', preset: 'custom' })}
                >
                  WebGL
                </button>
                <button
                  className={`${styles.toggleBtn} ${config.rendererType === 'canvas2d' ? styles.active : ''}`}
                  onClick={() => setConfig({ rendererType: 'canvas2d', preset: 'custom' })}
                >
                  Canvas2D
                </button>
              </div>
              <span className={styles.settingHint}>
                WebGL is faster but may have blur issues. Canvas2D is more stable and compatible.
              </span>
            </div>
          </div>

          {/* Video Resolution Section */}
          {process.env.REACT_APP_ENV === 'local' && (
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>Video Output</h4>
              <p className={styles.sectionNote}>
                Adjust video resolution for meetings. Higher resolution uses more bandwidth.
              </p>

              {/* Video Resolution */}
              <div className={styles.settingRow}>
                <div className={styles.settingHeader}>
                  <label>Video Resolution</label>
                </div>
                <select
                  value={selectedVideoResolution}
                  onChange={(e) => setVideoResolution(e.target.value as TVideoResolutionPreset)}
                  className={styles.select}
                >
                  {VIDEO_RESOLUTION_PRESETS.map((preset) => (
                    <option key={preset.value} value={preset.value}>
                      {preset.label}
                    </option>
                  ))}
                </select>
                <span className={styles.settingHint}>
                  Select your preferred video quality. Lower resolutions reduce bandwidth and CPU usage.
                </span>
              </div>
            </div>
          )}

          {/* Footer with CPU Usage and Actions */}
          <div className={styles.footer}>
            <div className={styles.cpuIndicator}>
              <span className={styles.cpuLabel}>Estimated CPU Usage:</span>
              <span
                className={`${styles.cpuValue} ${cpuUsage > 50 ? styles.high : cpuUsage > 25 ? styles.medium : styles.low}`}
              >
                ~{cpuUsage}%
              </span>
            </div>
            <div className={styles.actions}>
              <button className={styles.resetBtn} onClick={handleReset}>
                Reset to Defaults
              </button>
            </div>
          </div>
        </div>
      </OPopper>
    </>
  );
};
